import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from '@app/app.module';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

const GOOGLE_MAPS_SCRIPT_ID = 'mkc-google-maps-places-script';
const GOOGLE_MAPS_CALLBACK_NAME = '__mkcGoogleMapsPlacesLoaded';

function getGoogleMapsNamespace(): any {
  const googleWindow = window as any;
  googleWindow.google = googleWindow.google || {};
  googleWindow.google.maps = googleWindow.google.maps || {};

  return googleWindow.google.maps;
}

function googleMapsPlacesReady(): boolean {
  const googleWindow = window as any;
  return !!(
    googleWindow.google &&
    googleWindow.google.maps &&
    googleWindow.google.maps.places &&
    googleWindow.google.maps.places.Autocomplete
  );
}

function importGooglePlaces(): Promise<void> {
  const maps = getGoogleMapsNamespace();

  if (maps.importLibrary) {
    return maps.importLibrary('places').then(() => undefined);
  }

  if (googleMapsPlacesReady()) {
    return Promise.resolve();
  }

  return Promise.reject(new Error('Google Maps importLibrary is not available.'));
}

function loadGoogleMapsPlaces(): Promise<void> {
  if (googleMapsPlacesReady()) {
    return Promise.resolve();
  }

  if (!environment.GOOGLE_MAPS_BROWSER_KEY) {
    return Promise.reject(new Error('Google Maps browser key is not configured.'));
  }

  const maps = getGoogleMapsNamespace();

  if (maps.importLibrary) {
    return importGooglePlaces();
  }

  const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID) as HTMLScriptElement;
  if (existingScript) {
    return new Promise<void>((resolve, reject) => {
      if (googleMapsPlacesReady()) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => importGooglePlaces().then(resolve).catch(reject));
      existingScript.addEventListener('error', () => reject(new Error('Google Maps JavaScript API failed to load.')));
    });
  }

  const script = document.createElement('script');
  const params = [
    'key=' + encodeURIComponent(environment.GOOGLE_MAPS_BROWSER_KEY),
    'loading=async',
    'callback=google.maps.' + GOOGLE_MAPS_CALLBACK_NAME,
    'language=en'
  ];

  script.id = GOOGLE_MAPS_SCRIPT_ID;
  script.src = 'https://maps.googleapis.com/maps/api/js?' + params.join('&');
  script.async = true;

  return new Promise<void>((resolve, reject) => {
    maps[GOOGLE_MAPS_CALLBACK_NAME] = () => {
      delete maps[GOOGLE_MAPS_CALLBACK_NAME];
      importGooglePlaces().then(resolve).catch(reject);
    };
    script.addEventListener('error', () => reject(new Error('Google Maps JavaScript API failed to load.')));
    document.head.appendChild(script);
  });
}

function bootstrapApplication() {
  return platformBrowserDynamic().bootstrapModule(AppModule, {
    preserveWhitespaces: true
  });
}

loadGoogleMapsPlaces()
  .then(() => bootstrapApplication())
  .catch(err => console.log(err));
